import { supabase } from '../lib/supabase'

export type ContractScopeType = 'single_property' | 'customer_properties'

export interface ContractPropertyLink {
  id: string
  contract_id: string
  property_id: string
  effective_from: string
  effective_to?: string | null
  status: 'active' | 'ended'
  is_primary: boolean
  property?: {
    id: string
    name?: string | null
    address?: string | null
    customer_id?: string | null
  } | null
}

export interface PropertyUnit {
  id: string
  property_id: string
  unit_number: string
  normalized_unit_number: string
  display_label?: string | null
  default_rate_description?: string | null
  bedrooms?: number | null
  bathrooms?: number | null
  is_active: boolean
}

export function normalizeUnitNumber(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '')
}

export async function fetchContractProperties(
  contractId: string,
  activeOnly = false
): Promise<ContractPropertyLink[]> {
  let query = supabase
    .from('contract_properties')
    .select(
      'id, contract_id, property_id, effective_from, effective_to, status, is_primary, property:properties(id, name, address, customer_id)'
    )
    .eq('contract_id', contractId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true })
  if (activeOnly) query = query.eq('status', 'active')
  const { data, error } = await query
  if (error) throw error
  return (data || []).map((row: any) => ({
    ...row,
    property: Array.isArray(row.property) ? row.property[0] || null : row.property,
  }))
}

export async function syncContractProperties(
  contractId: string,
  propertyIds: string[],
  primaryPropertyId?: string | null
): Promise<void> {
  const uniqueIds = Array.from(new Set(propertyIds.filter(Boolean)))
  const { data: existing, error: existingError } = await supabase
    .from('contract_properties')
    .select('id, property_id, status')
    .eq('contract_id', contractId)
  if (existingError) throw existingError

  const existingByProperty = new Map((existing || []).map((row) => [row.property_id, row]))
  const today = new Date().toISOString().slice(0, 10)

  const { error: clearPrimaryError } = await supabase
    .from('contract_properties')
    .update({ is_primary: false, updated_at: new Date().toISOString() })
    .eq('contract_id', contractId)
    .eq('is_primary', true)
  if (clearPrimaryError) throw clearPrimaryError

  for (const propertyId of uniqueIds) {
    const row = existingByProperty.get(propertyId)
    if (row) {
      const { error } = await supabase
        .from('contract_properties')
        .update({
          status: 'active',
          effective_to: null,
          is_primary: propertyId === primaryPropertyId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.id)
      if (error) throw error
    } else {
      const { error } = await supabase.from('contract_properties').insert({
        contract_id: contractId,
        property_id: propertyId,
        effective_from: today,
        status: 'active',
        is_primary: propertyId === primaryPropertyId,
      })
      if (error) throw error
    }
  }

  const removed = (existing || []).filter(
    (row) => row.status === 'active' && !uniqueIds.includes(row.property_id)
  )
  if (removed.length > 0) {
    const { error } = await supabase
      .from('contract_properties')
      .update({ status: 'ended', effective_to: today, is_primary: false })
      .in(
        'id',
        removed.map((row) => row.id)
      )
    if (error) throw error
  }
}

export async function fetchPropertyUnits(propertyId: string): Promise<PropertyUnit[]> {
  if (!propertyId) return []
  const { data, error } = await supabase
    .from('property_units')
    .select('*')
    .eq('property_id', propertyId)
    .eq('is_active', true)
    .order('unit_number')
  if (error) throw error
  return data || []
}

export async function upsertPropertyUnit(params: {
  propertyId: string
  unitNumber: string
  rateDescription: string
}): Promise<PropertyUnit> {
  const normalized = normalizeUnitNumber(params.unitNumber)
  const { data, error } = await supabase
    .from('property_units')
    .upsert(
      {
        property_id: params.propertyId,
        unit_number: params.unitNumber.trim(),
        normalized_unit_number: normalized,
        display_label: params.unitNumber.trim(),
        default_rate_description: params.rateDescription,
        is_active: true,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'property_id,normalized_unit_number' }
    )
    .select()
    .single()
  if (error) throw error
  return data
}
